/* eslint-disable max-lines */
import { useSOP, useUpdateLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import {
  LocationAlarmCamera,
  QuickResolveActionItem,
  Situation,
  SOPWorkflow,
  StatusType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { SOPWorkflowStateContext, toast } from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import { SOPManager } from '../sop-manager/SOPManager';
import { getQuickResolveActionLabel } from '../sop-manager/type-guards';
import { useAlarmGroupStatic } from '../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { useAlarmGroupTracking } from '../unified-alarm-group/useAlarmGroupTracking';
import AIRecommendation from './ai-recommendation/AIRecommendation';
import { EnterpriseAlarmCard } from './EnterpriseAlarmCard';
import { EscalateAlarm } from './escalate/EscalateAlarm';
import { FloatingModalContainer } from './FloatingModalContainer';
import { ResolveAction } from './ResolveAction';
import { SectionStateProvider } from './SectionStateContext';
import { SOPWorkflowRoot } from './SOPWorkflowRoot';
import {
  UnauthorizedFace,
  UnauthorizedFaceDetection,
} from './unauthorized-face-detection';
import { currentStepAtom, updateDefaultValues, WorkflowItemType } from './util';
import { createSectionsContent, useShouldHideAIRecommendation } from './utils';
import WorkflowHeader from './WorkflowHeader';

interface Props {
  locationId: string;
  locationAlarmId: string;
  alarmCameras: LocationAlarmCamera[];
  onAlarmResolve: (status: StatusType, comment?: string) => void;
  locationTenantId: string;
  alarmGroupStatus?: string;
  escalatedAlarmId?: string;
  product?: UnifiedMonitoringProduct;
  onEscalate: (
    comment: string,
    situation?: string,
    subSituation?: string
  ) => void;
  onQuickResolve?: (
    comment: string,
    action?: string,
    subAction?: string
  ) => void;
  onCallFromSOP?: (twilioCallSid: string, toName: string) => void;
  escalationId?: string;
  locationTimezone: string;
  unauthorizedFaces?: UnauthorizedFace[];
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
}

export function SOPWorkflowContainer(props: Props) {
  const {
    locationId,
    locationAlarmId,
    alarmCameras,
    onAlarmResolve,
    escalatedAlarmId,
    alarmGroupStatus,
    locationTenantId,
    product,
    onEscalate,
    onQuickResolve,
    onCallFromSOP,
    escalationId,
    locationTimezone,
    unauthorizedFaces,
    alarmStartEpochMs,
    alarmEndEpochMs,
  } = props;
  const { dataVersion } = useAlarmGroupStatic();
  const track = useAlarmGroupTracking();
  const isScanContext = product === UnifiedMonitoringProduct.SCAN;
  const [sopWorkflow, setSOPWorkflow] = useState<SOPWorkflow>();
  const [selectedActionWithSubactions, setSelectedActionWithSubactions] =
    useState<QuickResolveActionItem | undefined>();
  const [selectedSituation, setSelectedSituation] = useState<
    Situation | undefined
  >();
  const [currentAtomStep, setCurrentAtomStep] = useAtom(currentStepAtom);
  const shouldHideAIRecommendation = useShouldHideAIRecommendation(
    locationAlarmId,
    isScanContext
      ? UnifiedMonitoringProduct.SCAN
      : UnifiedMonitoringProduct.REMOTE_GUARDING,
    unauthorizedFaces
  );

  const resolveMutation = useUpdateLocationAlarm(
    isScanContext ? '' : locationAlarmId,
    {
      onSuccess: () => {
        toast('Alarm status updated', { type: 'success' });
        onAlarmResolve(StatusType.RESOLVED);
      },
      dataVersion: isScanContext ? undefined : dataVersion,
    }
  );

  const onSubmitResolveAction = async (
    comment: string,
    action?: QuickResolveActionItem,
    subAction?: string
  ) => {
    track('alarm_group_sop_resolve_submit', {
      ...(action && { action: getQuickResolveActionLabel(action) }),
      ...(subAction && { subAction }),
    });
    if (isScanContext) {
      const actionLabel =
        action && typeof action === 'object' && 'label' in action
          ? action.label
          : typeof action === 'string'
          ? action
          : '';
      onQuickResolve?.(comment, actionLabel, subAction);
    } else {
      resolveMutation.mutate({ status: StatusType.RESOLVED, comment });
    }
  };

  const onQuickResolveSubmit = (comment: string) => {
    track('alarm_group_sop_quick_resolve_select', { action: comment });
    if (isScanContext) {
      onQuickResolve?.(comment);
    } else {
      onSubmitResolveAction(comment);
    }
  };
  const {
    isLoading,
    isRefetching,
    isError,
    error,
    data: sopData,
  } = useSOP(
    {
      locationId,
    },
    async ({ sop_workflow: workflow }) => {
      if (workflow) {
        const updatedWorkflow = updateDefaultValues(workflow);
        setSOPWorkflow(updatedWorkflow);
      }
    }
  );
  const isCurrentAlarmEscalated =
    escalatedAlarmId && escalatedAlarmId === locationAlarmId;
  const [currentWorkflowItemType, setCurrentWorkflowItemType] = useState<
    WorkflowItemType | undefined
  >(
    isCurrentAlarmEscalated ? WorkflowItemType.ESCALATE : WorkflowItemType.STEPS
  );

  useEffect(() => {
    setCurrentAtomStep(0);
  }, [locationAlarmId, setCurrentAtomStep]);

  useEffect(() => {
    if (
      alarmGroupStatus &&
      alarmGroupStatus.toLowerCase().includes('resolved')
    ) {
      setCurrentWorkflowItemType(undefined);
    }
  }, [alarmGroupStatus]);

  useEffect(() => {
    if (
      isCurrentAlarmEscalated &&
      !alarmGroupStatus?.toLowerCase().includes('resolved')
    ) {
      setCurrentWorkflowItemType(WorkflowItemType.ESCALATE);
    }
  }, [isCurrentAlarmEscalated, alarmGroupStatus]);

  useEffect(() => {
    if (
      currentAtomStep === 0 &&
      currentWorkflowItemType === WorkflowItemType.STEPS
    ) {
      setSelectedSituation(undefined);
    }
  }, [currentAtomStep, currentWorkflowItemType]);

  useEffect(() => {
    if (sopData) {
      if (sopData.sop_workflow) {
        const updatedWorkflow = updateDefaultValues(sopData.sop_workflow);
        setSOPWorkflow(updatedWorkflow);
      }
    }
  }, [sopData]);

  const navigateTo = (val: WorkflowItemType, action?: string) => () => {
    track('alarm_group_sop_navigate', { to: val, ...(action && { action }) });
    setCurrentWorkflowItemType(val);
    if (val === WorkflowItemType.STEPS && currentAtomStep === 0) {
      setSelectedSituation(undefined);
    }
  };

  const navigateToResolve = (action?: QuickResolveActionItem) => {
    navigateTo(
      WorkflowItemType.RESOLVE,
      action && getQuickResolveActionLabel(action)
    )();
    setSelectedActionWithSubactions(action);
  };

  const onStepChange = (step: number) => {
    track('alarm_group_sop_step_change', { step });
    setCurrentAtomStep(step);
    if (step === 0) {
      setSelectedSituation(undefined);
    }
  };

  const onUpdateSituation = (selectedSituationVal: Situation) => {
    track('alarm_group_sop_situation_select', {
      situation: selectedSituationVal.label,
    });
    setSelectedSituation(selectedSituationVal);
  };

  const logCallFromSOP = (twilioCallSid: string, contactTag?: string) => {
    track('alarm_group_sop_call_contact', { tag: contactTag });
    onCallFromSOP?.(twilioCallSid, contactTag ?? '');
  };

  const onPreEscalationSubmit = (
    comment: string,
    situation?: string,
    subSituation?: string
  ) => {
    track('alarm_group_sop_pre_escalate_submit', {
      situation,
      subSituation,
    });
    onEscalate(comment, situation, subSituation);
  };

  const handleQuickResolveFromHeader = (
    actionLabel: string,
    action?: QuickResolveActionItem
  ) => {
    if (actionLabel.toLowerCase() === 'others') {
      navigateToResolve();
      return;
    }

    // Check if action has subactions - navigate to resolve page with action
    if (
      action &&
      action !== null &&
      typeof action === 'object' &&
      'subActions' in action &&
      action.subActions &&
      action.subActions.length > 0
    ) {
      navigateToResolve(action);
      return;
    }

    onQuickResolveSubmit(actionLabel);
  };

  const isResolved = !!alarmGroupStatus?.toLowerCase().includes('resolved');

  // Header, recommendation, enterprise details and faces all appear under the
  // same condition. They are wrapped in one fragment below rather than tested
  // four times, so the guarantee is structural -- a fifth element added inside
  // inherits it instead of needing to remember the check.
  const showWorkflowChrome =
    currentWorkflowItemType !== WorkflowItemType.RESOLVE &&
    Boolean(sopWorkflow);

  const sectionsContent = createSectionsContent(sopWorkflow);

  return (
    <SectionStateProvider>
      <SOPWorkflowStateContext.Provider value={{ logCallFromSOP }}>
        <div className="relative min-h-[40rem] min-w-32 px-5 pb-4">
          {(isError || sopWorkflow === undefined) && (
            <div className="mx-auto max-w-screen-2xl p-6">
              {error?.message && (
                <Alert type="error">{error.message ?? ''}</Alert>
              )}
              {!(isLoading || isRefetching) && (
                <span>Please ask your admin to update the SOP.</span>
              )}
            </div>
          )}
          {(isLoading || isRefetching) && (
            <div className="absolute inset-0 z-10 flex h-full items-center justify-center p-2 opacity-70">
              <LoadingIndicator />
            </div>
          )}

          {sopWorkflow && (
            <div
              className={clsx(
                currentWorkflowItemType === WorkflowItemType.STEPS &&
                  'flex min-h-[25rem] flex-col items-center justify-between gap-3'
              )}
            >
              {alarmGroupStatus &&
                alarmGroupStatus.toLowerCase().includes('resolved') && (
                  <div className="m-4 p-4">
                    This alarm group is already resolved
                  </div>
                )}

              <div className="w-full">
                {showWorkflowChrome && (
                  <>
                    {!isResolved && (
                      <div
                        className={clsx(
                          'sticky top-0 z-10 -mx-5',
                          sopWorkflow.isZeroTolerance
                            ? 'mb-4 px-5 pb-2 pt-4'
                            : 'bg-onlight-bg-1 dark:bg-ondark-bg-1 mb-3 px-5 pt-1'
                        )}
                      >
                        <WorkflowHeader
                          currentStep={
                            currentWorkflowItemType === WorkflowItemType.STEPS
                              ? currentAtomStep
                              : 0
                          }
                          onStepChange={
                            currentWorkflowItemType === WorkflowItemType.STEPS
                              ? onStepChange
                              : undefined
                          }
                          isZeroToleranceSite={sopWorkflow.isZeroTolerance}
                          showQuickSafeInSOP={!isCurrentAlarmEscalated}
                          onQuickResolve={handleQuickResolveFromHeader}
                          quickResolveActions={sopWorkflow.quickResolveActions}
                          currentWorkflowItemType={currentWorkflowItemType}
                          navigateTo={navigateTo}
                          locationId={locationId}
                          sopNotes={sopWorkflow.notes}
                        />
                      </div>
                    )}

                    <AIRecommendation
                      alarmId={locationAlarmId}
                      product={
                        isScanContext
                          ? UnifiedMonitoringProduct.SCAN
                          : UnifiedMonitoringProduct.REMOTE_GUARDING
                      }
                      timeZone={locationTimezone}
                      hideDetails={shouldHideAIRecommendation}
                    />

                    <EnterpriseAlarmCard timeZone={locationTimezone} />

                    {unauthorizedFaces && unauthorizedFaces.length > 0 && (
                      <UnauthorizedFaceDetection
                        faces={unauthorizedFaces}
                        timeZone={locationTimezone}
                      />
                    )}
                  </>
                )}

                {isResolved && (
                  <div className="min-w-0 overflow-x-clip [overflow-wrap:anywhere]">
                    <SOPManager
                      sopId={sopData?.id ?? ''}
                      sopWorkflow={sopWorkflow}
                      showTitle={false}
                      fullWidth
                      compact
                    />
                  </div>
                )}

                {currentWorkflowItemType === WorkflowItemType.STEPS &&
                  (alarmCameras.length > 0 ? (
                    <SOPWorkflowRoot
                      navigateToEscalate={navigateTo(WorkflowItemType.ESCALATE)}
                      navigateToResolve={navigateToResolve}
                      sopWorkflow={sopWorkflow}
                      alarmCameras={alarmCameras}
                      locationAlarmId={locationAlarmId}
                      onQuickResolve={onQuickResolveSubmit}
                      isResolveSubmitting={resolveMutation.isLoading}
                      selectedSituation={selectedSituation}
                      onUpdateSituation={onUpdateSituation}
                      locationId={locationId}
                      product={product}
                      alarmStartEpochMs={alarmStartEpochMs}
                      alarmEndEpochMs={alarmEndEpochMs}
                      locationTimezone={locationTimezone}
                    />
                  ) : (
                    <div className="text-muted-foreground p-6 text-center text-base">
                      SOP content is not currently visible because no alarms are
                      selected.
                      <div className="mt-4">
                        To view the SOP, please enable minor alarms from the
                        timeline controls.
                      </div>
                    </div>
                  ))}
                {currentWorkflowItemType === WorkflowItemType.RESOLVE && (
                  <div className="pt-4">
                    <ResolveAction
                      onClose={navigateTo(WorkflowItemType.STEPS)}
                      onSubmitResolveAction={onSubmitResolveAction}
                      isLoading={resolveMutation.isLoading}
                      actionWithSubactions={selectedActionWithSubactions}
                    />
                  </div>
                )}
                {currentWorkflowItemType === WorkflowItemType.ESCALATE && (
                  <EscalateAlarm
                    escalationPoints={sopWorkflow.escalationPoints}
                    escalationProtocol={sopWorkflow.escalationProtocol || []}
                    isEscalated={!!isCurrentAlarmEscalated}
                    locationAlarmId={locationAlarmId}
                    alarmGroupId={isScanContext ? locationAlarmId : undefined}
                    locationId={locationId}
                    siteAddress={sopWorkflow.siteAddress}
                    locationTenantId={locationTenantId}
                    onCancel={navigateTo(WorkflowItemType.STEPS)}
                    selectedSituation={selectedSituation}
                    onPreEscalationSubmit={onPreEscalationSubmit}
                    product={product}
                    escalationId={escalationId}
                    mapEmbedUrl={sopData?.map_embed_url}
                    timezone={locationTimezone}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <FloatingModalContainer sectionsContent={sectionsContent} />
      </SOPWorkflowStateContext.Provider>
    </SectionStateProvider>
  );
}
